import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "modal", "modalTitle", "list",
    "itemIndex", "titleInput", "descriptionInput",
    "assigneeSelect", "prioritySelect", "dueDateInput",
    "deleteSection"
  ]

  static values = {
    meetingId: Number,
    csrfToken: String
  }

  connect() {
    console.log("ActionItemsController connected")
    document.addEventListener("keydown", this.handleKeydown.bind(this))
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown.bind(this))
  }

  handleKeydown(event) {
    if (event.key === "Escape" && this.hasModalTarget && !this.modalTarget.classList.contains("hidden")) {
      this.closeModal()
    }
  }

  openModal(event) {
    const button = event.currentTarget
    const itemIndex = button.dataset.itemIndex
    const itemRow = button.closest(".action-item-row")

    if (!itemRow) {
      console.error("Could not find action-item-row")
      return
    }

    // Populate form with existing data
    this.itemIndexTarget.value = itemIndex
    this.titleInputTarget.value = itemRow.dataset.itemTitle || ""
    this.descriptionInputTarget.value = itemRow.dataset.itemDescription || ""
    this.assigneeSelectTarget.value = itemRow.dataset.itemAssignee || ""
    this.prioritySelectTarget.value = itemRow.dataset.itemPriority || "medium"
    this.dueDateInputTarget.value = itemRow.dataset.itemDueDate || ""

    this.modalTitleTarget.textContent = "Edit Action Item"
    if (this.hasDeleteSectionTarget) {
      this.deleteSectionTarget.classList.remove("hidden")
    }

    this.modalTarget.classList.remove("hidden")
    this.titleInputTarget.focus()
  }

  addNew() {
    // Reset form for new item
    this.itemIndexTarget.value = "-1"
    this.titleInputTarget.value = ""
    this.descriptionInputTarget.value = ""
    this.assigneeSelectTarget.value = ""
    this.prioritySelectTarget.value = "medium"
    this.dueDateInputTarget.value = ""

    this.modalTitleTarget.textContent = "Add Action Item"
    if (this.hasDeleteSectionTarget) {
      this.deleteSectionTarget.classList.add("hidden")
    }

    this.modalTarget.classList.remove("hidden")
    this.titleInputTarget.focus()
  }

  closeModal() {
    this.modalTarget.classList.add("hidden")
  }

  async save(event) {
    event.preventDefault()

    const itemIndex = parseInt(this.itemIndexTarget.value, 10)
    const data = {
      item_index: itemIndex,
      title: this.titleInputTarget.value.trim(),
      description: this.descriptionInputTarget.value.trim(),
      assignee_name: this.assigneeSelectTarget.value,
      priority: this.prioritySelectTarget.value,
      due_date: this.dueDateInputTarget.value || null
    }

    if (!data.title) {
      alert("Please enter a title for the action item.")
      return
    }

    try {
      const endpoint = itemIndex === -1 
        ? `/meetings/${this.meetingIdValue}/add_action_item`
        : `/meetings/${this.meetingIdValue}/update_action_item`
      
      const response = await fetch(endpoint, {
        method: itemIndex === -1 ? "POST" : "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue,
          "Accept": "application/json"
        },
        body: JSON.stringify(data)
      })

      if (response.ok) {
        this.closeModal()
        window.location.reload()
      } else {
        const error = await response.json()
        alert(error.error || "Failed to save action item.")
      }
    } catch (error) {
      console.error("Error saving action item:", error)
      alert("Failed to save action item. Please try again.")
    }
  }

  async deleteItem() {
    const itemIndex = parseInt(this.itemIndexTarget.value, 10)

    if (!confirm("Are you sure you want to remove this action item?")) {
      return
    }

    try {
      const response = await fetch(`/meetings/${this.meetingIdValue}/delete_action_item`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue,
          "Accept": "application/json"
        },
        body: JSON.stringify({ item_index: itemIndex })
      })

      if (response.ok) {
        this.closeModal()
        window.location.reload()
      } else {
        const error = await response.json()
        alert(error.error || "Failed to delete action item.")
      }
    } catch (error) {
      console.error("Error deleting action item:", error)
      alert("Failed to delete action item. Please try again.")
    }
  }

  async quickDelete(event) {
    const button = event.currentTarget
    const itemIndex = parseInt(button.dataset.itemIndex, 10)
    const itemRow = button.closest(".action-item-row")
    const itemTitle = itemRow?.dataset.itemTitle || "this action item"

    if (!confirm(`Delete "${itemTitle}"?`)) {
      return
    }

    // Show deleting state
    const originalHtml = button.innerHTML
    button.innerHTML = `
      <svg class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    `
    button.disabled = true

    try {
      const response = await fetch(`/meetings/${this.meetingIdValue}/delete_action_item`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue,
          "Accept": "application/json"
        },
        body: JSON.stringify({ item_index: itemIndex })
      })

      if (response.ok) {
        // Animate removal
        if (itemRow) {
          itemRow.style.transition = "opacity 0.2s, transform 0.2s"
          itemRow.style.opacity = "0"
          itemRow.style.transform = "translateX(-10px)"
          setTimeout(() => {
            itemRow.remove()
            // Check if list is now empty
            if (this.hasListTarget && this.listTarget.children.length === 0) {
              window.location.reload()
            }
          }, 200)
        } else {
          window.location.reload()
        }
      } else {
        const error = await response.json()
        alert(error.error || "Failed to delete action item.")
        button.innerHTML = originalHtml
        button.disabled = false
      }
    } catch (error) {
      console.error("Error deleting action item:", error)
      alert("Failed to delete action item. Please try again.")
      button.innerHTML = originalHtml
      button.disabled = false
    }
  }

  async createTodo(event) {
    const button = event.currentTarget
    const itemIndex = button.dataset.itemIndex
    const itemRow = button.closest(".action-item-row")

    if (!itemRow) {
      console.error("Could not find action-item-row")
      return
    }

    const data = {
      item_index: parseInt(itemIndex, 10),
      title: itemRow.dataset.itemTitle,
      description: itemRow.dataset.itemDescription || "",
      due_date: itemRow.dataset.itemDueDate || null,
      assignee_name: itemRow.dataset.itemAssignee || null
    }

    try {
      const response = await fetch(`/meetings/${this.meetingIdValue}/create_todo_from_action_item`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue,
          "Accept": "application/json"
        },
        body: JSON.stringify(data)
      })

      if (response.ok) {
        const result = await response.json()
        // Show success feedback
        button.innerHTML = `
          <svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
        `
        button.disabled = true
        setTimeout(() => {
          button.innerHTML = `
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
          `
          button.disabled = false
        }, 2000)
      } else {
        const error = await response.json()
        alert(error.error || "Failed to create todo.")
      }
    } catch (error) {
      console.error("Error creating todo:", error)
      alert("Failed to create todo. Please try again.")
    }
  }
}

